/**
 * Confirm step of the New swarm create flow.
 *
 * Persona list stays compact; click a row to expand it in the shared confirm
 * column with use-cases and goals. Nothing is persisted until "Create & launch".
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ChevronDown, Loader2, X } from "lucide-react";
import { useQuery } from "convex/react";
import { Button } from "@mcpjam/design-system/button";
import { Input } from "@mcpjam/design-system/input";
import { JudgesSection } from "@/components/evals/judges-section";
import { areAllChecksValid } from "@/components/evals/checks-section";
import { JourneyRubricEditor } from "@/components/swarms/journey-rubric-editor";
import {
  PersonaPixelAvatar,
  mintPersonaAvatarLook,
} from "@/components/swarms/persona-pixel-avatar";
import {
  estimateLaunchSessions,
  type SwarmIntensityPreset,
} from "@/components/swarms/swarm-intensity";
import { SWARM_QUERIES } from "@/lib/swarm-api";
import { useAvailableModels } from "@/hooks/use-available-models";
import type { GoalJudgeConfig } from "@/components/shared/session-quality/judge-config";
import {
  formatCriterion,
  SWARM_LEVEL_PREDICATE_KINDS,
} from "@/shared/predicate-kinds";
import {
  MAX_RUBRIC_CRITERIA,
  mintCriterionId,
  type JourneyCriterion,
} from "@/shared/journey-rubric";
import { cn } from "@/lib/utils";

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  );
}

/** A generated persona + journeys, still only in memory. */
export type ProposedPersona = {
  /** Stable local key — these rows have no `_id` until launch. */
  key: string;
  name: string;
  role: string;
  notes?: string;
  avatarShape: number;
  avatarPalette: number;
  journeys: {
    key: string;
    name?: string;
    goal: string;
    /** Generation-suggested checks, stamped onto THIS journey's rubric only —
     * ids minted at proposal time so the row shown here is the row stamped. */
    checks?: JourneyCriterion[];
  }[];
};

/** An existing persona the user folded into this swarm. */
export type ReusedPersona = {
  _id: string;
  name: string;
  role: string;
  notes: string;
  avatarShape?: number;
  avatarPalette?: number;
};

/** One journey to launch, with the persona it belongs to (for the Running matrix). */
export type LaunchTarget = {
  journeyId: string;
  label: string;
  personaId: string;
  personaName: string;
  personaRole: string;
  avatarShape?: number;
  avatarPalette?: number;
  /** Stored env fan-out of a REUSED journey (`null` = legacy client-based).
   * Launch compares this to the Describe selection to decide whether the
   * journey must be re-stamped before launching. Absent on created targets —
   * they are born with the selection. */
  environmentIds?: string[] | null;
  /** Stored sessions-per-target of a REUSED journey (`null` = the row carries
   * no config). Launch does not rewrite a shared journey's config, so this —
   * not the intensity preset — is what the run will execute. Absent on created
   * targets, which are born with the preset's config. */
  sessionsPerTarget?: number | null;
};

export type ConfirmLaunchPayload = {
  rubric: JourneyCriterion[];
  judgeConfig?: GoalJudgeConfig;
  /** Existing journeys to launch. When the Describe step carries an explicit
   * environment selection, launch stamps it onto any of these whose stored
   * environments differ — the picker's promise wins over the journey's past. */
  reusedTargets: LaunchTarget[];
  /** Per reused journey: its current rubric. Swarm-level grading is MERGED
   * into it at launch (additive, structural dedupe) — same "this screen's
   * promise wins" rule as environments, minus the overwrite. */
  reusedGrading: { journeyId: string; existingRubric: JourneyCriterion[] }[];
};

type SelectedPersona =
  | { kind: "proposed"; key: string }
  | { kind: "reused"; id: string };

/**
 * Checks a fresh slate starts with, honoring the Describe step's "we infer …
 * a scoring rubric" promise. Confirm is a prune screen, so these arrive
 * pre-filled the same way personas do — and they're limited to the universal
 * instruments that hold for ANY server: no tool names, no thresholds to
 * guess, free to grade. Journey-specific checks (naming actual tools) belong
 * to generation and land with the backend follow-up.
 */
function starterRubric(): JourneyCriterion[] {
  return [
    { id: mintCriterionId(), predicate: { type: "noToolErrors" } },
    {
      id: mintCriterionId(),
      predicate: { type: "finalAssistantMessageNonEmpty" },
    },
  ];
}

type ReusedGoal = {
  journeyId: string;
  label: string;
};

type ReusedResolved = {
  targets: LaunchTarget[] | null;
  goals: ReusedGoal[];
  graded: boolean;
  /** Each journey's CURRENT rubric, so launch can merge swarm-level criteria
   * into it instead of overwriting what its owner authored. */
  grading: { journeyId: string; rubric: JourneyCriterion[] }[];
};

function journeyLabel(journey: { name?: string; goal: string }): string {
  const name = journey.name?.trim();
  if (name) return name;
  const goal = journey.goal.trim();
  return goal.length > 48 ? `${goal.slice(0, 47)}…` : goal;
}

function personaContext(notes?: string, role?: string): string {
  return notes?.trim() || role?.trim() || "No use cases or context yet.";
}

function CompactPersonaCard({
  seed,
  name,
  role,
  description,
  meta,
  selected,
  onSelect,
  onRemove,
  removeLabel,
  avatarShape,
  avatarPalette,
}: {
  seed: string;
  name: string;
  role: string;
  description: string;
  meta: string;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  removeLabel: string;
  avatarShape?: number;
  avatarPalette?: number;
}) {
  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        data-testid="new-swarm-persona-compact"
        onClick={onSelect}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect();
          }
        }}
        className={cn(
          "flex w-full cursor-pointer items-start gap-3 rounded-xl border bg-muted/15 px-3 py-3 text-left transition-colors",
          selected
            ? "border-primary/50 bg-muted/30 ring-1 ring-primary/25"
            : "border-border/50 hover:bg-muted/25"
        )}
      >
        <PersonaPixelAvatar
          seed={seed}
          shapeIndex={avatarShape}
          paletteIndex={avatarPalette}
          size="md"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="min-w-0 truncate text-sm font-semibold text-foreground">
              {name}
              {role ? (
                <span className="font-normal text-muted-foreground">
                  {" "}
                  — {role}
                </span>
              ) : null}
            </p>
          </div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {description}
          </p>
          <p className="font-mono text-[11px] leading-snug text-muted-foreground">
            {meta}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 shrink-0 px-2 text-xs"
          aria-label={removeLabel}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          Remove
        </Button>
      </div>
    </li>
  );
}

function PersonaDetailPanel({
  seed,
  name,
  role,
  context,
  goals,
  graded,
  loadingGoals,
  draftEditable,
  onClose,
  onRemove,
  removeLabel,
  onRemoveGoal,
  onEditGoal,
  onRemoveCheck,
  onChangeName,
  onChangeRole,
  onChangeContext,
  onChangeGoal,
  onAddGoal,
  avatarShape,
  avatarPalette,
}: {
  seed: string;
  name: string;
  role: string;
  context: string;
  goals: {
    key: string;
    label: string;
    editable?: boolean;
    /** Suggested deterministic checks scoped to this goal's journey. */
    checks?: { id: string; label: string }[];
  }[];
  graded?: boolean;
  loadingGoals?: boolean;
  /** Proposed personas are editable in-place; existing ones are not. */
  draftEditable?: boolean;
  onClose: () => void;
  onRemove: () => void;
  removeLabel: string;
  onRemoveGoal?: (goalKey: string) => void;
  /** Opens the Personas tab to edit this goal's journey (existing only). */
  onEditGoal?: (goalKey: string) => void;
  onRemoveCheck?: (goalKey: string, checkId: string) => void;
  onChangeName?: (name: string) => void;
  onChangeRole?: (role: string) => void;
  onChangeContext?: (notes: string) => void;
  onChangeGoal?: (goalKey: string, goal: string) => void;
  onAddGoal?: () => void;
  avatarShape?: number;
  avatarPalette?: number;
}) {
  return (
    <div
      className="rounded-xl border border-primary/50 bg-muted/30 ring-1 ring-primary/25"
      data-testid="new-swarm-persona-detail"
    >
      <div className="flex items-start justify-between gap-3 px-4 pt-3">
        <PersonaPixelAvatar
          seed={seed}
          shapeIndex={avatarShape}
          paletteIndex={avatarPalette}
          size="md"
        />
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            aria-label={removeLabel}
            onClick={onRemove}
          >
            Remove
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="size-8 p-0 text-muted-foreground"
            aria-label="Close persona detail"
            onClick={onClose}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </div>

      <div className="space-y-4 px-4 py-4">
        <div className="space-y-1.5">
          <SectionLabel>Persona</SectionLabel>
          {draftEditable ? (
            <div className="space-y-2 rounded-xl border border-border/50 bg-background p-3">
              <Input
                value={name}
                onChange={(event) => onChangeName?.(event.target.value)}
                placeholder="Name"
                aria-label="Persona name"
                className="h-8"
              />
              <Input
                value={role}
                onChange={(event) => onChangeRole?.(event.target.value)}
                placeholder="Role"
                aria-label="Persona role"
                className="h-8"
              />
            </div>
          ) : (
            <div className="flex min-w-0 items-center gap-2 rounded-full border border-border/50 bg-background px-3 py-1.5">
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                {name}
                {role ? (
                  <span className="font-normal text-muted-foreground">
                    {" "}
                    — {role}
                  </span>
                ) : null}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <SectionLabel>Use cases & context</SectionLabel>
          {draftEditable ? (
            <textarea
              value={context === "No use cases or context yet." ? "" : context}
              onChange={(event) => onChangeContext?.(event.target.value)}
              placeholder="Who they are and how they show up…"
              aria-label="Use cases and context"
              rows={4}
              className="w-full resize-none rounded-xl border border-border/50 bg-background px-3 py-2.5 text-sm leading-relaxed text-foreground outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30"
            />
          ) : (
            <div className="rounded-xl border border-border/50 bg-background px-3 py-2.5">
              <p className="text-sm leading-relaxed text-muted-foreground">
                {context}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <SectionLabel>Goals</SectionLabel>
          {loadingGoals ? (
            <p className="text-sm text-muted-foreground">Loading goals…</p>
          ) : goals.length === 0 && !draftEditable ? (
            <p className="rounded-xl border border-dashed border-border/60 px-3 py-2.5 text-sm text-muted-foreground">
              No goals yet — this persona has nothing to run.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {goals.map((goal) => (
                <li
                  key={goal.key}
                  className="group rounded-xl border border-border/50 bg-background px-3 py-2"
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className="size-1.5 shrink-0 rounded-full bg-primary"
                      aria-hidden
                    />
                    {draftEditable && onChangeGoal ? (
                      <Input
                        value={goal.label}
                        onChange={(event) =>
                          onChangeGoal(goal.key, event.target.value)
                        }
                        placeholder="What should they try to do?"
                        aria-label="Goal"
                        className="h-7 min-w-0 flex-1 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                      />
                    ) : (
                      <span className="min-w-0 flex-1 text-sm leading-snug text-foreground">
                        {goal.label}
                      </span>
                    )}
                    <span className="flex shrink-0 items-center gap-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                      {goal.editable && onEditGoal ? (
                        <button
                          type="button"
                          aria-label={`Edit goal ${goal.label}`}
                          className="text-xs text-muted-foreground hover:text-foreground"
                          data-testid="new-swarm-goal-edit"
                          onClick={() => onEditGoal(goal.key)}
                        >
                          edit
                        </button>
                      ) : null}
                      {onRemoveGoal ? (
                        <button
                          type="button"
                          aria-label={`Remove goal ${goal.label}`}
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => onRemoveGoal(goal.key)}
                        >
                          <X className="size-3.5" />
                        </button>
                      ) : null}
                    </span>
                  </div>
                  {/* Journey-scoped checks live with the journey they grade,
                      not in the swarm-level rubric below the persona list —
                      stamping them there would drag down pass rates on
                      journeys that never touch the tool. */}
                  {goal.checks && goal.checks.length > 0 ? (
                    <ul
                      className="mt-1.5 flex flex-wrap gap-1.5 pl-4"
                      aria-label={`Suggested checks for ${goal.label}`}
                    >
                      {goal.checks.map((check) => (
                        <li
                          key={check.id}
                          data-testid="new-swarm-journey-check"
                          className="flex items-center gap-1 rounded-full border border-border/50 bg-muted/30 px-2 py-0.5 text-[11px] text-muted-foreground"
                        >
                          {check.label}
                          {onRemoveCheck ? (
                            <button
                              type="button"
                              aria-label={`Remove check ${check.label}`}
                              className="text-muted-foreground hover:text-foreground"
                              onClick={() => onRemoveCheck(goal.key, check.id)}
                            >
                              <X className="size-3" />
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {draftEditable && onAddGoal ? (
            <button
              type="button"
              onClick={onAddGoal}
              data-testid="new-swarm-add-goal"
              className="w-full rounded-xl border border-dashed border-border/60 px-3 py-2 text-left text-sm text-muted-foreground hover:border-border hover:text-foreground"
            >
              + Add a goal
            </button>
          ) : null}
          {graded ? (
            <p className="text-xs text-muted-foreground">
              Has its own grading — swarm-level checks are merged in at
              launch, never replacing it.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Keeps journeys for one reused persona subscribed and reports them upward.
 * Mounted for every reused row so expand/collapse never drops the query.
 */
function ReusedPersonaJourneyLoader({
  persona,
  onResolved,
}: {
  persona: ReusedPersona;
  onResolved: (personaId: string, data: ReusedResolved) => void;
}) {
  const journeys = useQuery(
    SWARM_QUERIES.listJourneysByPersona as any,
    { personaRefId: persona._id } as any
  ) as
    | {
        _id: string;
        name?: string;
        goal: string;
        rubric?: JourneyCriterion[] | null;
        judgeConfig?: GoalJudgeConfig;
        environmentIds?: string[] | null;
        config?: { sessionsPerTarget?: number; maxTurns?: number } | null;
      }[]
    | undefined;

  const resolved = useMemo((): ReusedResolved => {
    if (journeys === undefined) {
      return { targets: null, goals: [], graded: false, grading: [] };
    }
    return {
      grading: journeys.map((journey) => ({
        journeyId: journey._id,
        rubric: journey.rubric ?? [],
      })),
      targets: journeys.map((journey) => ({
        journeyId: journey._id,
        label: `${persona.name} · ${journeyLabel(journey)}`,
        personaId: persona._id,
        personaName: persona.name,
        personaRole: persona.role,
        environmentIds: journey.environmentIds ?? null,
        sessionsPerTarget: journey.config?.sessionsPerTarget ?? null,
        ...(persona.avatarShape !== undefined
          ? { avatarShape: persona.avatarShape }
          : {}),
        ...(persona.avatarPalette !== undefined
          ? { avatarPalette: persona.avatarPalette }
          : {}),
      })),
      goals: journeys.map((journey) => ({
        journeyId: journey._id,
        label: journeyLabel(journey),
      })),
      graded: journeys.some(
        (journey) =>
          (journey.rubric && journey.rubric.length > 0) || journey.judgeConfig
      ),
    };
  }, [
    journeys,
    persona._id,
    persona.name,
    persona.role,
    persona.avatarShape,
    persona.avatarPalette,
  ]);

  useEffect(() => {
    onResolved(persona._id, resolved);
  }, [onResolved, persona._id, resolved]);

  return null;
}

/**
 * Compact selectable row for an existing persona folded into this swarm.
 */
function ReusedPersonaCard({
  persona,
  selected,
  onSelect,
  onRemove,
  resolved,
}: {
  persona: ReusedPersona;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  resolved: ReusedResolved | undefined;
}) {
  const goalCount = resolved?.goals.length;
  const meta =
    resolved == null || resolved.targets === null
      ? "Loading goals…"
      : [
          `${goalCount} ${goalCount === 1 ? "goal" : "goals"}`,
          "existing",
          resolved.graded ? "own grading" : null,
        ]
          .filter(Boolean)
          .join(" · ");

  return (
    <CompactPersonaCard
      seed={persona._id}
      name={persona.name}
      role={persona.role}
      description={personaContext(persona.notes, persona.role)}
      meta={meta}
      selected={selected}
      onSelect={onSelect}
      onRemove={onRemove}
      removeLabel={`Remove ${persona.name} from this swarm`}
      avatarShape={persona.avatarShape}
      avatarPalette={persona.avatarPalette}
    />
  );
}

export function NewSwarmConfirmStep({
  projectId,
  proposed,
  onProposedChange,
  reusedPersonas,
  onRemoveReused,
  preset,
  environmentCount,
  environmentLabels,
  launching,
  errorMessage,
  onBack,
  onLaunch,
  onEditExistingPersona,
}: {
  projectId: string;
  proposed: ProposedPersona[];
  onProposedChange: (next: ProposedPersona[]) => void;
  reusedPersonas: ReusedPersona[];
  onRemoveReused: (personaId: string) => void;
  preset: SwarmIntensityPreset;
  environmentCount: number;
  /** Display names of the environments this launch will fan out across. */
  environmentLabels: string[];
  launching: boolean;
  errorMessage: string | null;
  onBack: () => void;
  onLaunch: (payload: ConfirmLaunchPayload) => void;
  /** Leave the create flow and open Personas for an existing persona. */
  onEditExistingPersona: (personaRefId: string) => void;
}) {
  const [judgeConfig, setJudgeConfig] = useState<GoalJudgeConfig | undefined>(
    undefined
  );
  // Always seeded: swarm-level grading applies to every journey this swarm
  // launches — created ones get it stamped, reused ones get it MERGED into
  // their own rubric (existing rows survive; structural dupes are skipped).
  // Lazy init on purpose: Back discards all grading state anyway.
  const [rubric, setRubric] = useState<JourneyCriterion[]>(() =>
    starterRubric()
  );
  const [gradingOpen, setGradingOpen] = useState(false);
  const [selected, setSelected] = useState<SelectedPersona | null>(null);
  const [reusedResolved, setReusedResolved] = useState<
    Record<string, ReusedResolved>
  >({});
  const { availableModels } = useAvailableModels({ projectId });

  const handleReusedResolved = useCallback(
    (personaId: string, data: ReusedResolved) => {
      setReusedResolved((current) => {
        const previous = current[personaId];
        const prevTargets = previous?.targets;
        const nextTargets = data.targets;
        const targetsMatch =
          prevTargets === nextTargets ||
          (prevTargets != null &&
            nextTargets != null &&
            prevTargets.length === nextTargets.length &&
            prevTargets.every(
              (entry, index) =>
                entry.journeyId === nextTargets[index]?.journeyId &&
                // The stored sessions ride the target and drive the estimate,
                // so an id-only comparison would keep quoting a stale number
                // after someone edits that goal's sessions mid-flow.
                entry.sessionsPerTarget ===
                  nextTargets[index]?.sessionsPerTarget
            ));
        const goalsMatch =
          previous != null &&
          previous.goals.length === data.goals.length &&
          previous.goals.every(
            (goal, index) =>
              goal.journeyId === data.goals[index]?.journeyId &&
              goal.label === data.goals[index]?.label
          );
        if (
          previous &&
          previous.graded === data.graded &&
          targetsMatch &&
          goalsMatch
        ) {
          return current;
        }
        return { ...current, [personaId]: data };
      });
    },
    []
  );

  const newLocalKey = (prefix: string) =>
    `${prefix}-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

  const patchProposed = (
    key: string,
    patch: (persona: ProposedPersona) => ProposedPersona
  ) => {
    onProposedChange(
      proposed.map((persona) => (persona.key === key ? patch(persona) : persona))
    );
  };

  const removePersona = (key: string) => {
    onProposedChange(proposed.filter((persona) => persona.key !== key));
    setSelected((current) =>
      current?.kind === "proposed" && current.key === key ? null : current
    );
  };
  const removeJourney = (personaKey: string, journeyKey: string) => {
    // Keep an empty draft persona on the board — the user may still be
    // authoring goals. Launch simply skips personas with no journeys.
    patchProposed(personaKey, (persona) => ({
      ...persona,
      journeys: persona.journeys.filter((journey) => journey.key !== journeyKey),
    }));
  };
  const removeJourneyCheck = (
    personaKey: string,
    journeyKey: string,
    checkId: string
  ) => {
    patchProposed(personaKey, (persona) => ({
      ...persona,
      journeys: persona.journeys.map((journey) =>
        journey.key === journeyKey
          ? {
              ...journey,
              checks: (journey.checks ?? []).filter(
                (check) => check.id !== checkId
              ),
            }
          : journey
      ),
    }));
  };
  const addPersona = () => {
    const key = newLocalKey("persona");
    const next: ProposedPersona = {
      key,
      name: "New persona",
      role: "Role",
      ...mintPersonaAvatarLook(),
      journeys: [
        {
          key: newLocalKey("journey"),
          goal: "",
        },
      ],
    };
    onProposedChange([...proposed, next]);
    setSelected({ kind: "proposed", key });
  };
  const addGoal = (personaKey: string) => {
    patchProposed(personaKey, (persona) => ({
      ...persona,
      journeys: [
        ...persona.journeys,
        { key: newLocalKey("journey"), goal: "" },
      ],
    }));
  };
  const removeReused = (personaId: string) => {
    onRemoveReused(personaId);
    setSelected((current) =>
      current?.kind === "reused" && current.id === personaId ? null : current
    );
  };

  const reusedPending = reusedPersonas.some(
    (persona) => (reusedResolved[persona._id]?.targets ?? null) === null
  );
  const activeReusedTargets = reusedPersonas.flatMap(
    (persona) => reusedResolved[persona._id]?.targets ?? []
  );
  // Empty draft goals stay visible for authoring but don't count toward
  // launch readiness — Create & launch only persists trimmed goals.
  const newJourneyCount = proposed.reduce(
    (sum, persona) =>
      sum + persona.journeys.filter((journey) => journey.goal.trim()).length,
    0
  );
  const personaCount = proposed.length + reusedPersonas.length;
  const journeyCount = newJourneyCount + activeReusedTargets.length;
  // Every journey this launch fans out, not just the newly authored ones —
  // a reuse-heavy swarm was under-reporting its own session count. Reused
  // journeys are counted at THEIR OWN sessions, which is what launch runs
  // them at; the preset only sizes the journeys this swarm creates.
  const sessionEstimate = estimateLaunchSessions({
    preset,
    newJourneyCount,
    reusedSessionsPerTarget: activeReusedTargets.map(
      (target) => target.sessionsPerTarget ?? null
    ),
    environmentCount,
  });
  /**
   * Rubric budget held back for per-journey suggested checks. Launch stamps
   * the swarm rubric FIRST and appends each journey's own checks after, then
   * hard-slices at the cap — so without this reserve a full swarm rubric is
   * exactly what silently drops the tool-specific checks generation produced.
   * Reserve the worst case (the journey carrying the most checks), since the
   * cap applies per journey, not across the swarm.
   */
  const reservedCheckSlots = proposed.reduce(
    (worst, persona) =>
      persona.journeys.reduce(
        (inner, journey) => Math.max(inner, journey.checks?.length ?? 0),
        worst
      ),
    0
  );
  const rubricValid = areAllChecksValid(rubric.map((entry) => entry.predicate));
  const canLaunch =
    journeyCount > 0 && rubricValid && !launching && !reusedPending;

  const selectedProposed =
    selected?.kind === "proposed"
      ? proposed.find((persona) => persona.key === selected.key) ?? null
      : null;
  const selectedReused =
    selected?.kind === "reused"
      ? reusedPersonas.find((persona) => persona._id === selected.id) ?? null
      : null;

  // Drop stale selection if the persona was removed elsewhere.
  useEffect(() => {
    if (selected?.kind === "proposed" && !selectedProposed) {
      setSelected(null);
    }
    if (selected?.kind === "reused" && !selectedReused) {
      setSelected(null);
    }
  }, [selected, selectedProposed, selectedReused]);

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-y-auto"
      data-testid="new-swarm-confirm-step"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-8 sm:px-8">
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground">
            Here&rsquo;s who we&rsquo;ll send in.
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {personaCount} {personaCount === 1 ? "persona" : "personas"} ·{" "}
            {journeyCount} {journeyCount === 1 ? "goal" : "goals"}
            {sessionEstimate > 0
              ? ` · ${sessionEstimate} new ${
                  sessionEstimate === 1 ? "session" : "sessions"
                }`
              : ""}
            . Select a persona for details, or remove anything that
            doesn&rsquo;t fit.
          </p>
          {environmentLabels.length > 0 && proposed.length > 0 ? (
            <p
              className="text-sm leading-relaxed text-muted-foreground"
              data-testid="new-swarm-confirm-clients"
            >
              New goals run on{" "}
              <span className="font-medium text-foreground">
                {environmentLabels.join(" · ")}
              </span>
              {environmentLabels.length === 1
                ? " — pick more environments on Describe to compare clients."
                : "."}
            </p>
          ) : null}
        </div>

        {proposed.length > 0 ? (
          <ul className="space-y-2" data-testid="new-swarm-proposed-personas">
            {proposed.map((persona) => {
              const isSelected =
                selected?.kind === "proposed" && selected.key === persona.key;
              if (isSelected) {
                return (
                  <li key={persona.key}>
                    <PersonaDetailPanel
                      seed={persona.key}
                      name={persona.name}
                      role={persona.role}
                      context={persona.notes ?? ""}
                      goals={persona.journeys.map((journey) => ({
                        key: journey.key,
                        label: journey.goal,
                        ...(journey.checks && journey.checks.length > 0
                          ? {
                              checks: journey.checks.map((check) => ({
                                id: check.id,
                                label: formatCriterion(check),
                              })),
                            }
                          : {}),
                      }))}
                      draftEditable
                      avatarShape={persona.avatarShape}
                      avatarPalette={persona.avatarPalette}
                      onClose={() => setSelected(null)}
                      onRemove={() => removePersona(persona.key)}
                      removeLabel={`Remove persona ${persona.name}`}
                      onRemoveGoal={(goalKey) =>
                        removeJourney(persona.key, goalKey)
                      }
                      onRemoveCheck={(goalKey, checkId) =>
                        removeJourneyCheck(persona.key, goalKey, checkId)
                      }
                      onChangeName={(nextName) =>
                        patchProposed(persona.key, (current) => ({
                          ...current,
                          name: nextName,
                        }))
                      }
                      onChangeRole={(nextRole) =>
                        patchProposed(persona.key, (current) => ({
                          ...current,
                          role: nextRole,
                        }))
                      }
                      onChangeContext={(notes) =>
                        patchProposed(persona.key, (current) => ({
                          ...current,
                          notes,
                        }))
                      }
                      onChangeGoal={(goalKey, goal) =>
                        patchProposed(persona.key, (current) => ({
                          ...current,
                          journeys: current.journeys.map((journey) =>
                            journey.key === goalKey
                              ? { ...journey, goal }
                              : journey
                          ),
                        }))
                      }
                      onAddGoal={() => addGoal(persona.key)}
                    />
                  </li>
                );
              }
              const goalCount = persona.journeys.length;
              return (
                <CompactPersonaCard
                  key={persona.key}
                  seed={persona.key}
                  name={persona.name}
                  role={persona.role}
                  description={personaContext(persona.notes, persona.role)}
                  meta={`${goalCount} ${
                    goalCount === 1 ? "goal" : "goals"
                  } · new`}
                  selected={false}
                  onSelect={() =>
                    setSelected({ kind: "proposed", key: persona.key })
                  }
                  onRemove={() => removePersona(persona.key)}
                  removeLabel={`Remove persona ${persona.name}`}
                  avatarShape={persona.avatarShape}
                  avatarPalette={persona.avatarPalette}
                />
              );
            })}
          </ul>
        ) : null}

        {reusedPersonas.length > 0 ? (
          <>
            {reusedPersonas.map((persona) => (
              <ReusedPersonaJourneyLoader
                key={`load-${persona._id}`}
                persona={persona}
                onResolved={handleReusedResolved}
              />
            ))}
            <ul className="space-y-2" data-testid="new-swarm-reused-personas">
              {reusedPersonas.map((persona) => {
                const isSelected =
                  selected?.kind === "reused" && selected.id === persona._id;
                if (isSelected) {
                  return (
                    <li key={persona._id}>
                      <PersonaDetailPanel
                        seed={persona._id}
                        name={persona.name}
                        role={persona.role}
                        context={personaContext(
                          persona.notes,
                          persona.role
                        )}
                        goals={(reusedResolved[persona._id]?.goals ?? []).map(
                          (goal) => ({
                            key: goal.journeyId,
                            label: goal.label,
                            editable: true,
                          })
                        )}
                        graded={reusedResolved[persona._id]?.graded}
                        loadingGoals={
                          (reusedResolved[persona._id]?.targets ?? null) ===
                          null
                        }
                        avatarShape={persona.avatarShape}
                        avatarPalette={persona.avatarPalette}
                        onClose={() => setSelected(null)}
                        onRemove={() => removeReused(persona._id)}
                        removeLabel={`Remove ${persona.name} from this swarm`}
                        onEditGoal={() =>
                          onEditExistingPersona(persona._id)
                        }
                      />
                    </li>
                  );
                }
                return (
                  <ReusedPersonaCard
                    key={persona._id}
                    persona={persona}
                    selected={false}
                    onSelect={() =>
                      setSelected({ kind: "reused", id: persona._id })
                    }
                    onRemove={() => removeReused(persona._id)}
                    resolved={reusedResolved[persona._id]}
                  />
                );
              })}
            </ul>
          </>
        ) : null}

        <button
          type="button"
          onClick={addPersona}
          data-testid="new-swarm-add-persona"
          className="self-start text-sm font-medium text-primary hover:text-primary/80"
        >
          + Add persona
        </button>

        {/* Scoring applies to EVERY journey this swarm launches: stamped
            onto created journeys, merged additively into reused ones (their
            own rows survive with their ids). Always visible for the same
            reason the environment picker is — this screen's promise wins. */}
        <div className="border-t border-border/40 pt-3">
          <button
            type="button"
            onClick={() => setGradingOpen((open) => !open)}
            className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
            aria-expanded={gradingOpen}
            data-testid="new-swarm-grading-toggle"
          >
            <ChevronDown
              className={cn(
                "size-3.5 transition-transform",
                gradingOpen && "rotate-180"
              )}
            />
            Grading
            {(() => {
              // Same invitation the header line makes with "2 personas ·
              // 10 journeys": say what's inside before it's opened.
              const journeyCheckCount = proposed.reduce(
                (sum, persona) =>
                  sum +
                  persona.journeys.reduce(
                    (inner, journey) =>
                      inner + (journey.checks?.length ?? 0),
                    0
                  ),
                0
              );
              const summary = [
                judgeConfig ? "judge on" : null,
                rubric.length > 0
                  ? `${rubric.length} ${
                      rubric.length === 1 ? "check" : "checks"
                    }`
                  : null,
                journeyCheckCount > 0
                  ? `${journeyCheckCount} goal-specific`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ");
              return summary ? (
                <span className="font-normal text-muted-foreground/80">
                  · {summary}
                </span>
              ) : null;
            })()}
          </button>
          {gradingOpen ? (
            <div className="mt-2">
              <JudgesSection
                chrome="bare"
                value={judgeConfig}
                onChange={setJudgeConfig}
                availableModels={availableModels}
                bareAutoGradeBlurb="Grade every session in this swarm automatically against its goal. Uses credits. You can also judge any session on demand from its detail view."
                bareAutoGradeAriaLabel="Auto-grade every session with LLM as Judge"
              />
              <div className="mt-3 border-t border-border/40 pt-3">
                <JourneyRubricEditor
                  value={rubric}
                  onChange={setRubric}
                  allowedKinds={SWARM_LEVEL_PREDICATE_KINDS}
                  maxCriteria={MAX_RUBRIC_CRITERIA - reservedCheckSlots}
                />
                {reservedCheckSlots > 0 ? (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Goal-specific checks were also suggested — they
                    appear with each persona&rsquo;s goals, and grade only
                    their own goal. {reservedCheckSlots}{" "}
                    {reservedCheckSlots === 1 ? "slot is" : "slots are"}{" "}
                    reserved for them.
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {errorMessage ? (
          <p role="alert" className="text-sm leading-relaxed text-destructive">
            {errorMessage}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button
            type="button"
            disabled={!canLaunch}
            data-testid="new-swarm-launch"
            onClick={() =>
              onLaunch({
                rubric,
                ...(judgeConfig ? { judgeConfig } : {}),
                reusedTargets: activeReusedTargets,
                reusedGrading: reusedPersonas.flatMap((persona) =>
                  (reusedResolved[persona._id]?.grading ?? []).map(
                    (entry) => ({
                      journeyId: entry.journeyId,
                      existingRubric: entry.rubric,
                    })
                  )
                ),
              })
            }
          >
            {launching ? (
              <>
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                Creating &amp; launching…
              </>
            ) : (
              "Create & launch"
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={launching}
            onClick={onBack}
          >
            Back
          </Button>
        </div>
      </div>
    </div>
  );
}
